import { Button } from '@/shared/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/shared/components/ui/form'
import { SegmentedControl } from '@/shared/components/ui/segmented-control'
import { Textarea } from '@/shared/components/ui/textarea'
import { Typography } from '@/shared/components/ui/typography'
import {
  useFeatureFlags,
  useFeatureFlagsWithWorkspace,
} from '@/shared/hooks/useFeatureFlags'
import { cn } from '@/shared/lib/utils'
import { zodResolver } from '@hookform/resolvers/zod'
import AttachFileOutlined from '@mui/icons-material/AttachFileOutlined'
import KeyboardArrowUpOutlined from '@mui/icons-material/KeyboardArrowUpOutlined'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import PublicIcon from '@mui/icons-material/Public'
import React, { forwardRef, useCallback, useEffect, useRef } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { ExampleButtons } from './ExampleButtons'
import { FileDropzone } from './FileDropzone'
import { FilePreview } from './FilePreview'
import { DEFAULT_ACCEPTED_FILE_TYPES, PromptTag } from './constants'

import BoltIconOutline from '@mui/icons-material/BoltOutlined'
import BookOutlinedIcon from '@mui/icons-material/BookOutlined'

import { useUpgradePlanModal } from '@/shared/hooks/useUpgradePlanModal'
import {
  getPromptFormSchema,
  type PromptFormData,
  type PromptFormProps,
} from './types'
import { useAnimatedPlaceholder } from './useAnimatedPlaceholder'

export const PromptForm = forwardRef<HTMLTextAreaElement, PromptFormProps>(
  function PromptForm(
    {
      onSubmit,
      className = '',
      variant = 'homepage',
      maxFiles = 5,
      maxFileSize = 10,
      acceptedFileTypes = DEFAULT_ACCEPTED_FILE_TYPES,
      showExamples,
      disabled,
      hideVisibilityControl,
      placeholder,
      value,
    },
    ref
  ) {
    const [selectedTag, setSelectedTag] = React.useState<PromptTag | null>(
      PromptTag.PLAYBOOKS
    )
    const { show: showUpgradePlanModal } = useUpgradePlanModal()
    const [selectedExample, setSelectedExample] = React.useState<{
      title: string
      value: string
    } | null>(null)

    const [isPlaybookVisibilityManagementEnabled] = useFeatureFlags([
      'playbookVisibilityManagement',
    ])

    const {
      isLoading: isLoadingFeatureFlags,
      flags: [isFileUploadEnabled, isPrivateVisibilityEnabled],
    } = useFeatureFlagsWithWorkspace([
      'chatFileUpload',
      'playbookPrivateVisibility',
    ])
    const openFileInputRef = useRef<(() => void) | undefined>()

    const form = useForm<PromptFormData>({
      resolver: zodResolver(getPromptFormSchema(isPrivateVisibilityEnabled)),
      values: {
        query: '',
        files: [],
        ...value,
        visibility: isPrivateVisibilityEnabled
          ? (value?.visibility ?? 'public')
          : 'public',
      },
      disabled,
    })

    const {
      watch,
      setValue,
      formState: { isSubmitting },
    } = form
    const files = watch('files')

    const queryValue = watch('query')

    const animatedPlaceholderText = React.useMemo(() => {
      if (selectedExample) {
        return selectedExample.value
      }

      return 'What would you like to do with AI?'
    }, [selectedExample])

    const {
      placeholder: animatedPlaceholder,
      onFocus,
      onBlur,
    } = useAnimatedPlaceholder({
      value: queryValue,
      placeholder: animatedPlaceholderText,
      animationEnabled: !!selectedExample,
    })

    const handleFilesAdded = useCallback(
      (newFiles: File[]) => {
        setValue('files', [...files, ...newFiles])
      },
      [files, setValue]
    )

    const handleFileRemove = useCallback(
      (index: number) => {
        setValue(
          'files',
          files.filter((_, i) => i !== index)
        )
      },
      [files, setValue]
    )

    const onFormSubmit = useCallback(
      async (data: PromptFormData) => {
        if (data.query.trim() || data.files.length > 0) {
          await onSubmit(data)
          form.reset()
        }
      },
      [onSubmit, form]
    )

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.ctrlKey && e.key === 'Enter') {
          e.preventDefault()
          if (!disabled && !isSubmitting) {
            form.handleSubmit(onFormSubmit)()
          }
        }
      },
      [disabled, isSubmitting, form, onFormSubmit]
    )

    const handleFileInputClick = useCallback((openFn: () => void) => {
      openFileInputRef.current = openFn
    }, [])

    const handleExampleSelect = useCallback(
      (example: { title: string; value: string } | null) => {
        setSelectedExample(example)
      },
      []
    )

    const handleTagSelect = useCallback((tagId: string) => {
      if (!tagId || !Object.values(PromptTag).includes(tagId as PromptTag)) {
        return
      }
      setSelectedTag(tagId as PromptTag)
      setSelectedExample(null)
    }, [])

    const handleDisabledOption = () => {
      showUpgradePlanModal('limited_feature')
    }

    useEffect(() => {
      if (queryValue && queryValue.length > 0) {
        setSelectedExample(null)
      }
    }, [queryValue])

    return (
      <div className={`flex flex-col gap-6 ${className}`}>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onFormSubmit)}>
            <FileDropzone
              onFilesAdded={handleFilesAdded}
              maxFiles={maxFiles}
              maxFileSize={maxFileSize}
              acceptedFileTypes={acceptedFileTypes}
              disabled={disabled || !isFileUploadEnabled}
              onFileInputClick={handleFileInputClick}
              style={{
                outlineStyle: 'auto',
                outlineWidth: 0,
              }}
              className={cn(
                'relative w-full rounded-[20px] p-4 focus-within:outline focus-within:!outline-2 max-md:max-w-full',
                variant === 'homepage'
                  ? 'border bg-white shadow-prompt md:border-none md:shadow-promptMd'
                  : variant === 'dashboard'
                    ? 'border bg-grey-02'
                    : 'border bg-white'
              )}
            >
              <FormField
                control={form.control}
                name="query"
                render={({ field }) => (
                  <FormItem>
                    <FormControl ref={ref}>
                      <Textarea
                        {...field}
                        data-gramm="false"
                        id="search-input"
                        placeholder={placeholder ?? animatedPlaceholder}
                        className={cn(
                          'max-h-[320px] min-h-[80px] w-full resize-none overflow-auto !border-0 border-none bg-transparent',
                          'p-2 text-primary !shadow-none !outline-none focus:!border-0 focus:!shadow-none',
                          'focus:!outline-none focus-visible:!shadow-none focus-visible:!outline-none',
                          'placeholder:text-mydra-gray focus-visible:!outline-none',
                          variant === 'playbook'
                            ? 'text-sm font-medium'
                            : 'text-sm md:text-base'
                        )}
                        rows={variant === 'playbook' ? 2 : 3}
                        onKeyDown={handleKeyDown}
                        onFocus={onFocus}
                        onBlur={onBlur}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex items-center">
                <div className="flex w-full justify-between gap-2 self-start">
                  {isFileUploadEnabled && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0 rounded-full border p-1 focus-visible:outline-none"
                      type="button"
                      onClick={() => openFileInputRef.current?.()}
                      disabled={disabled}
                    >
                      <AttachFileOutlined
                        sx={{ fontSize: { xs: 16, md: 20 } }}
                      />
                    </Button>
                  )}

                  <div className="ml-auto flex gap-2">
                    {isPlaybookVisibilityManagementEnabled &&
                      !hideVisibilityControl && (
                        <Controller
                          control={form.control}
                          name="visibility"
                          render={({ field }) => (
                            <SegmentedControl.Root
                              type="single"
                              size="sm"
                              value={field.value}
                              onValueChange={(v) => {
                                if (v) {
                                  field.onChange(v)
                                }
                              }}
                              className="flex items-center bg-grey-03/50"
                            >
                              {[
                                {
                                  value: 'public',
                                  icon: PublicIcon,
                                  label: 'Public',
                                  disabled: isLoadingFeatureFlags,
                                },
                                {
                                  value: 'private',
                                  icon: LockOutlinedIcon,
                                  label: 'Private',
                                  disabled:
                                    isLoadingFeatureFlags ||
                                    !isPrivateVisibilityEnabled,
                                },
                              ].map(
                                ({ value, label, icon: Icon, disabled }) => (
                                  <div
                                    key={`isPublic--${value.toString()}`}
                                    className="h-6 cursor-pointer"
                                    onClick={() =>
                                      disabled && handleDisabledOption()
                                    }
                                  >
                                    <SegmentedControl.Item
                                      id={`isPublic--${value.toString()}`}
                                      value={value}
                                      className="overflow-hidden p-2 text-sm font-medium text-mydra-black data-[state=on]:bg-paper data-[state=on]:text-mydra-black"
                                      disabled={disabled}
                                    >
                                      <div className="flex items-center justify-center gap-1 text-sm leading-3">
                                        <Icon sx={{ fontSize: 16 }} />
                                        <span className="line-clamp-2">
                                          {label}
                                        </span>
                                      </div>
                                    </SegmentedControl.Item>
                                  </div>
                                )
                              )}
                            </SegmentedControl.Root>
                          )}
                        />
                      )}

                    <button
                      type="submit"
                      aria-label="Submit search"
                      className={cn(
                        'flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-black p-2 transition-colors hover:bg-gray-800 disabled:opacity-50',
                        isSubmitting && 'opacity-50'
                      )}
                      disabled={disabled || isSubmitting}
                    >
                      <KeyboardArrowUpOutlined
                        sx={{ fontSize: 20, color: 'white' }}
                      />
                    </button>
                  </div>
                </div>
              </div>

              {files.length > 0 && (
                <div className="my-2 space-y-1">
                  <Typography variant="caption" className="font-medium">
                    Attached files
                  </Typography>
                  {files.map((file, index) => (
                    <FilePreview
                      key={`${file.name}-${index}`}
                      file={file}
                      onRemove={() => handleFileRemove(index)}
                      variant={variant === 'playbook' ? 'dashboard' : variant}
                    />
                  ))}
                </div>
              )}
            </FileDropzone>
          </form>
        </Form>

        {showExamples && (
          <>
            <div className="flex justify-center">
              <SegmentedControl.Root
                type="single"
                size="sm"
                value={selectedTag || PromptTag.PLAYBOOKS}
                onValueChange={(value) => handleTagSelect(value)}
              >
                {[
                  {
                    value: PromptTag.PLAYBOOKS,
                    icon: BookOutlinedIcon,
                    label: 'AI Playbooks',
                  },
                  {
                    value: PromptTag.TOOL_DISCOVERY,
                    icon: (props) => (
                      <BoltIconOutline
                        {...props}
                        className={cn(
                          '!fill-transparent stroke-current stroke-2'
                        )}
                      />
                    ),
                    label: 'AI Tool Discovery',
                  },
                ].map(({ value, label, icon: Icon }) => (
                  <SegmentedControl.Item
                    id={value}
                    key={value}
                    value={value}
                    className="overflow-hidden px-4 py-2 text-sm font-medium"
                  >
                    <div className="flex items-center justify-center gap-1 text-sm leading-3">
                      <Icon sx={{ fontSize: 16 }} />
                      <span className="line-clamp-2">{label}</span>
                    </div>
                  </SegmentedControl.Item>
                ))}
              </SegmentedControl.Root>
            </div>

            <ExampleButtons
              selectedTag={selectedTag}
              onExampleSelect={handleExampleSelect}
              variant={variant}
              disabled={disabled}
            />
          </>
        )}
      </div>
    )
  }
)
